/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

//  calculate progress bar
export const getProgress = (testResults) => {
  if (!testResults?.length) return 0;
  const completedTests = new Set(testResults.map(r => r.test_name)).size;
  return (completedTests / 6) * 100; // 6 is total required tests
};

// Helper function to safely parse percentage values
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const getTestScore = (result) => {
  // Simply return the raw_percentage, with null as fallback
  return result.raw_percentage !== null && result.raw_percentage !== undefined 
    ? parseFloat(result.raw_percentage) 
    : null;
};

// Helper to get percentage from a test result
const calculatePercentage = (result) => {
  // First, try raw_percentage as it's pre-calculated
  if (result.raw_percentage != null) {
    const percentage = safeParseFloat(result.raw_percentage);
    if (percentage >= 0) return percentage;
  }
  
  // If no raw_percentage, calculate from score and total_questions
  if (result.score != null && result.total_questions > 0) {
    return (result.score / result.total_questions) * 100;
  }
  
  console.log('Unable to calculate percentage:', {
    raw: result.raw_percentage,
    score: result.score,
    total: result.total_questions
  });
  
  return 0;
};

export const getGroupedTestResults = (testResults) => {
  if (!testResults?.length) return {};
  
  return testResults.reduce((acc, result) => {
    if (!result.test_name) return acc;
    if (!acc[result.test_name]) {
      acc[result.test_name] = [];
    }
    
    const score = result.raw_percentage;
    acc[result.test_name].push({
      ...result,
      score: score,
      formatted_score: score === null ? 'No score' : `${parseFloat(score).toFixed(1)}%`
    });

    // Sort attempts by timestamp, most recent first
    acc[result.test_name].sort((a, b) => 
      new Date(b.attempt_timestamp) - new Date(a.attempt_timestamp)
    );
    
    return acc;
  }, {});
};

export const getAverageScore = (testResults) => {
  if (!testResults?.length) return 0;
  
  const validScores = testResults
    .map(result => result.raw_percentage)
    .filter(score => score !== null && score !== undefined)
    .map(score => parseFloat(score));
  
  if (!validScores.length) return 0;
  
  const average = validScores.reduce((sum, score) => sum + score, 0) / validScores.length;
  return Number(average.toFixed(2));
};

export const getBestScore = (attempts) => {
  if (!attempts?.length) return 0;
  return Math.max(...attempts.map(a => parseFloat(a.raw_percentage || a.score || 0)));
};
  
export const getPassingRate = (testResults) => {
  if (!testResults?.length) return 0;
    const uniqueTests = new Set(testResults.map(test => test.test_name));
    const passedTests = testResults.filter(test => test.passed).length;
    return passedTests > 0 ? calculatePercentage(passedTests, uniqueTests.size) : 'No passing tests';
  };
  
const formatTimeSpent = (seconds) => {
  if (!seconds) return '0m';
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m`;
};

export const isPassing = (score, passThreshold = 90) => {
  if (score === null || score === undefined) return false;
  const numericScore = typeof score === 'object'
    ? parseFloat(score.raw_percentage || score.score || 0)
    : parseFloat(score);
  return numericScore >= passThreshold;
};

export const getCompletionMetrics = (student, totalRequired = 6) => {
  if (!student?.test_results) {
    return { completed: 0, total: totalRequired, percentage: 0 };
  }
  // Group by test name and check for any passed attempts
  const testStatus = student.test_results.reduce((acc, result) => {
    if (!acc[result.test_name]) {
      acc[result.test_name] = false;
    }
    // Use the database 'passed' field which already considers the correct pass threshold
    if (result.passed === true) {
      acc[result.test_name] = true;
    }
    return acc;
  }, {});

  const completed = Object.values(testStatus).filter(Boolean).length;
  const percentage = calculatePercentage(completed, totalRequired);
  
  return { completed, total: totalRequired, percentage };
};

export const getStudentStats = (student) => {
  if (!student?.test_results?.length) {
    return {
      averageScore: 0,
      totalTests: 0,
      totalTime: '0m',
      lastTestDate: null,
      passingRate: 0,
      totalAttempts: 0,
      uniqueTestsAttempted: 0,
      bestScore: 0
    };
  }

  const testResults = student.test_results;
  const uniqueTests = new Set(testResults.map(test => test.test_name));

  const totalTimeSeconds = testResults.reduce((sum, test) => sum + (test.time_spent || 0), 0);

  return {
    averageScore: getAverageScore(testResults),
    totalTests: testResults.length,
    totalTime: formatTimeSpent(totalTimeSeconds),
    lastTestDate: testResults[0]?.attempt_timestamp || null,
    passingRate: getPassingRate(testResults),
    totalAttempts: testResults.length,
    uniqueTestsAttempted: uniqueTests.size,
    bestScore: getBestScore(testResults)
  };
};

export { safeParseFloat }